import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
	Bookmark,
	Car,
	FileText,
	Gavel,
	LayoutGrid,
	LifeBuoy,
	type LucideIcon,
	Receipt,
	Search,
	Settings,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { bidQueries, invoiceQueries, myVehicleQueries } from '@/api';
import { useWatchlist } from '@/components/domain/use-watchlist';
import { cn } from '@/lib/utils';
import { isActiveBid } from '@/utils/bid-status';

/**
 * Навігація робочого простору (макет Paper «Dashboard — A3»).
 *
 * Той самий перелік малюється і в бічній панелі на широкому екрані, і в
 * мобільній шухляді — звідси окремий компонент, а не розмітка всередині
 * каркаса.
 */

/**
 * Розділи панелі — літеральний союз, а не `string`: інакше `Link` втрачає
 * типізацію маршрутів і описка в адресі проходить компілятор мовчки.
 */
type Section = '/dashboard' | '/auctions' | '/watchlist' | '/bids' | '/vehicles' | '/payments' | '/contact';

type Item = {
	label: string;
	icon: LucideIcon;
	/**
	 * Без адреси — розділу ще немає: пункт видно з позначкою «soon».
	 * Стан виражений саме відсутністю адреси, а не окремим прапорцем, щоб
	 * не можна було оголосити «soon» із робочим посиланням чи навпаки.
	 */
	to?: Section;
	/** Звідки лічильник; без поля — пункт без числа. */
	count?: 'watchlist' | 'bids' | 'vehicles' | 'payments';
};

const WORKSPACE: Item[] = [
	{ to: '/dashboard', label: 'Overview', icon: LayoutGrid },
	{ to: '/auctions', label: 'Auctions', icon: Search },
	{ to: '/watchlist', label: 'Watchlist', icon: Bookmark, count: 'watchlist' },
	{ to: '/bids', label: 'My bids', icon: Gavel, count: 'bids' },
	{ to: '/vehicles', label: 'My vehicles', icon: Car, count: 'vehicles' },
	{ to: '/payments', label: 'Payments', icon: Receipt, count: 'payments' },
	{ label: 'Documents', icon: FileText },
];

const SUPPORT: Item[] = [
	{ label: 'Settings', icon: Settings },
	{ to: '/contact', label: 'Help & support', icon: LifeBuoy },
];

/**
 * Числа біля пунктів.
 *
 * Запити НЕ запускаються (`enabled: false`) — беремо лише те, що вже лежить
 * у кеші після Dashboard чи відповідної сторінки. Тягнути три повні списки
 * на кожній сторінці робочої зони заради трьох цифр у панелі — надто дорого;
 * порожнє місце чесніше за число, куплене зайвим запитом.
 */
function useCounts(): Record<NonNullable<Item['count']>, number | undefined> {
	const watchlist = useWatchlist();
	const bids = useQuery({ ...bidQueries.list(), enabled: false }).data;
	const vehicles = useQuery({ ...myVehicleQueries.list(), enabled: false }).data;
	const invoices = useQuery({ ...invoiceQueries.list(), enabled: false }).data;

	return {
		watchlist: watchlist.count,
		bids: bids?.filter(isActiveBid).length,
		// «Мої авто» — ті, що ще в дорозі: доставлене з панелі не смикає.
		vehicles: vehicles?.filter((vehicle) => vehicle.status !== 'DELIVERED').length,
		payments: invoices?.filter((invoice) => invoice.status !== 'PAID').length,
	};
}

/*
  Збіг НЕ точний (поведінка `Link` за замовчуванням): на `/vehicles/$id`
  лишається підсвіченим «My vehicles», і фільтри в адресі підсвітку не гасять.
  Жоден розділ панелі не є префіксом іншого, тож зайвого підсвічування
  це не дає.
*/
const ROW = 'flex h-11 items-center gap-3 rounded-lg px-3 text-sm';

function NavRow({ item, count, onNavigate }: { item: Item; count?: number; onNavigate?: () => void }): ReactNode {
	const Icon = item.icon;

	if (!item.to) {
		return (
			<span className={cn(ROW, 'cursor-default text-fg-subtle')} aria-disabled="true">
				<Icon className="size-4.5 shrink-0" strokeWidth={1.8} />
				<span className="flex-1">{item.label}</span>
				<span className="text-xs font-medium text-fg-subtle">soon</span>
			</span>
		);
	}

	return (
		<Link
			to={item.to}
			onClick={onNavigate}
			className={cn(ROW, 'text-fg transition-colors hover:bg-surface-muted')}
			activeProps={{ className: 'bg-nav-active text-primary font-semibold hover:bg-nav-active' }}
		>
			<Icon className="size-4.5 shrink-0" strokeWidth={1.8} />
			<span className="flex-1">{item.label}</span>
			{count !== undefined && count > 0 && (
				<span className="text-xs font-medium tabular-nums text-fg-muted">{count}</span>
			)}
		</Link>
	);
}

export function WorkspaceNav({ onNavigate }: { onNavigate?: () => void }): ReactNode {
	const counts = useCounts();

	return (
		<div className="flex h-full flex-col justify-between gap-6">
			<div className="flex flex-col gap-1">
				<p className="px-3 pb-3 text-[0.6875rem] font-medium tracking-[0.06em] text-fg-muted">WORKSPACE</p>
				{WORKSPACE.map((item) => (
					<NavRow
						key={item.label}
						item={item}
						count={item.count ? counts[item.count] : undefined}
						onNavigate={onNavigate}
					/>
				))}
			</div>

			<div className="flex flex-col gap-1">
				{SUPPORT.map((item) => (
					<NavRow key={item.label} item={item} onNavigate={onNavigate} />
				))}
			</div>
		</div>
	);
}
